import React from 'react';
import { WithTheme, makeCSS } from '../../../utils/makeCSS';
import { Theme } from '../../../theme/themes';
import { isMobile } from 'react-device-detect';

/**The NotificationItemLayout defines the layout of the NotificationItem
 * and the component that are similar to it but need a different alignment of the information that they're displaying. */
export interface NotificationItemLayoutProps {
  imageSection: React.ReactNode;
  //first row of the component
  topRowSection: React.ReactNode;
  //second row of the component
  bottomRowSection: React.ReactNode;
  //section to display a status or indicate something else
  indicatorSection?: React.ReactNode;
  //comment section
  dataSection?: React.ReactNode;
  //
  fullWidth?: boolean;
}

const useCSS = makeCSS(
  ({ theme, fullWidth }: WithTheme<Theme> & { fullWidth: boolean }) => {
    return {
      root: {
        display: 'flex',
        width: '100%',
        padding: theme.spacing[2],
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: theme.spacing[2],
        borderRadius: theme.borderRadius.lg
      },

      columns: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: theme.spacing[2]
      },
      detailsDiv: {
        display: 'flex',
        textWrap: 'nowrap',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: '1 0 0',
        alignSelf: 'stretch',
        width: theme.spacing[72]
      },
      detailsRow: {
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: theme.spacing[1],
        alignSelf: 'stretch',
        paddingBottom: isMobile ? 4 : '',
        '>*:last-child': {
          //put the name of the asset into new row
          flexBasis: isMobile ? '100%' : ''
        }
      },

      indicatorSection: {
        display: 'flex',
        alignItems: 'center'
      },
      commentDiv: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing[2],
        width: isMobile ? 'calc(100% - 30px)' : 334,
        border: `1px solid ${theme.color.line['03']}`,
        background: theme.color.overlay['01'],
        marginLeft: fullWidth ? 18 : ''
      }
    };
  }
);

export const NotificationItemLayout = (props: NotificationItemLayoutProps) => {
  const {
    imageSection,
    topRowSection,
    bottomRowSection,
    indicatorSection,
    dataSection,
    fullWidth
  } = props;

  const classes = useCSS(props);

  return (
    <div className={`${classes.root}`}>
      <div className={`${classes.columns}`}>
        {imageSection}
        <div className={`${classes.detailsDiv}`}>
          <div className={`${classes.detailsRow}`}>{topRowSection}</div>
          <div className={`${classes.detailsRow}`}>{bottomRowSection}</div>
        </div>
        <div className={`${classes.indicatorSection}`}>{indicatorSection}</div>
      </div>

      {dataSection && (
        <div className={`${classes.commentDiv}`}>{dataSection}</div>
      )}
    </div>
  );
};
